import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { assistantApi, jobsApi } from "../../services/api";
import { profileFields } from "../../services/assistant";
import { copyText, downloadResponse, downloadText } from "../../utils/download";
import { applyEdits } from "../../utils/resume";
import { formatDate } from "../../utils/format";
import { openApplication, openMessage } from "../../utils/extension";
import { questionStats } from "../../utils/jobs";
import QuestionsCard from "./QuestionsCard";
import AccessNotice, { UsageLeft } from "../../components/AccessNotice";

function ReadyItem({ ok, neutral, label, detail, fixTo, fixLabel }) {
  const icon = ok
    ? "bi-check-circle-fill text-success"
    : neutral
      ? "bi-dash-circle text-muted"
      : "bi-exclamation-circle-fill text-warning";
  return (
    <div className="ready-item">
      <i className={`bi ${icon}`} />
      <div className="flex-grow-1">
        <div className="small fw-medium">{label}</div>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {detail}
        </div>
      </div>
      {!ok && fixTo && (
        <Link to={fixTo} className="btn btn-sm btn-light">
          {fixLabel}
        </Link>
      )}
    </div>
  );
}

function CopyButton({ text, label = "Copy" }) {
  const { toast } = useApp();
  return (
    <button
      type="button"
      className="btn btn-sm btn-light flex-shrink-0"
      disabled={!text}
      onClick={async () => toast((await copyText(text)) ? "Copied" : "Couldn't copy", "info")}
      aria-label={label}
      title={label}
    >
      <i className="bi bi-clipboard" />
    </button>
  );
}

function ApplyTab({ job, onNext }) {
  const { profile, documents, completeStep, toast, downloadResume, downloadTranscript, integrations } = useApp();
  const [phase, setPhase] = useState("idle");
  const [fields, setFields] = useState([]);
  const [questionText, setQuestionText] = useState("");
  const [answers, setAnswers] = useState([]);
  const [writing, setWriting] = useState(false);
  const [opened, setOpened] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [opening, setOpening] = useState(false);

  const profileReady = !!(profile.firstName && profile.email && profile.phone);
  const defaultResume = documents.resumes.find((r) => r.isDefault) || documents.resumes[0];
  const transcript = documents.transcripts[documents.transcripts.length - 1];
  const company = job.company.replace(/\s+/g, "");

  const start = () => {
    setFields(profileFields(profile));
    setPhase("review");
  };

  const updateField = (id, value) => setFields((list) => list.map((f) => (f.id === id ? { ...f, value } : f)));

  const writeAnswers = async () => {
    const questions = questionText
      .split("\n")
      .map((q) => q.trim())
      .filter(Boolean);
    if (questions.length === 0) return;
    setWriting(true);
    try {
      const { answers: written } = await assistantApi.answers(job.id, questions);
      setAnswers(written);
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setWriting(false);
    }
  };

  const tailoredBase = job.tailoredResume && documents.resumes.find((r) => r.id === job.tailoredResume.baseResumeId);

  const downloadFile = async (kind) => {
    setDownloading(kind);
    try {
      const res = kind === "resume" ? await jobsApi.resumeFile(job.id, { format: "pdf" }) : await jobsApi.coverLetterFile(job.id);
      const { pages } = await downloadResponse(res, kind === "resume" ? "Resume.pdf" : `Cover_Letter_${company}.pdf`);
      if (kind === "resume" && pages > 1) toast(`Your tailored resume runs to ${pages} pages. Adjust the edits in the Resume tab.`, "warning");
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setDownloading("");
    }
  };

  // PDFs of tailored resumes need the Word original; otherwise offer the edited text.
  const downloadTailoredText = () => {
    const t = job.tailoredResume;
    const text = t.baseText
      ? applyEdits(t.baseText, t.suggestions).text
      : t.suggestions
          .filter((s) => s.accepted)
          .map((s) => s.after)
          .join("\n\n");
    downloadText(`Resume_${company}.txt`, text);
  };

  const submit = async () => {
    const wasFound = job.stage === "found";
    if (!(await completeStep(job.id, "applied", "Submitted application"))) return;
    toast(wasFound ? `Marked as applied. ${job.company} moved to Applied` : "Marked as applied");
    setPhase("idle");
  };

  if (job.steps?.applied && phase === "idle") {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <div className="success-icon mb-3">
            <i className="bi bi-send-check" />
          </div>
          <h2 className="h5">Application submitted</h2>
          <p className="text-muted small mb-4">
            {job.appliedAt ? `You applied on ${formatDate(job.appliedAt, { month: "long", day: "numeric" })}.` : "You've applied to this job."}{" "}
            Reaching out to the recruiter can double your chances of hearing back.
          </p>
          <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
            <button className="btn btn-primary" onClick={onNext}>
              Next: email the recruiter <i className="bi bi-arrow-right" />
            </button>
            <button className="btn btn-light" onClick={start}>
              Open application kit
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "review") {
    const missing = fields.filter((f) => !f.value).length;
    return (
      <div className="card">
        <div className="card-body">
          <div className="hint-box mb-4">
            <i className="bi bi-puzzle" />
            <span>
              <strong>Using Chrome?</strong> <Link to="/connect-extension">Install the Plateo extension</Link>, then click it on the application page to fill in
              these answers and attach your resume automatically. Everything below also works with copy and paste.
            </span>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <h2 className="h6 mb-0">Your answers</h2>
            {missing > 0 ? (
              <Link to="/profile" className="small">
                {missing} missing · complete profile
              </Link>
            ) : (
              <span className="badge bg-success-subtle text-success-emphasis">All set</span>
            )}
          </div>
          <div className="d-flex flex-column gap-2 mb-4">
            {fields.map((f) => (
              <div key={f.id} className={`field-row status-${f.value ? "filled" : "missing"}`}>
                <label className="small fw-medium mb-1 d-block" htmlFor={`f-${f.id}`}>
                  {f.label}
                </label>
                <div className="d-flex gap-2">
                  <input
                    id={`f-${f.id}`}
                    className="form-control form-control-sm"
                    value={f.value}
                    placeholder="Not in your profile"
                    onChange={(e) => updateField(f.id, e.target.value)}
                  />
                  <CopyButton text={f.value} label={`Copy ${f.label}`} />
                </div>
              </div>
            ))}
          </div>

          <h2 className="h6 mb-1">Questions on the application</h2>
          <p className="text-muted small mb-2">
            Paste the application's own questions, one per line, and we'll draft answers from your resume.
          </p>
          <textarea
            className="form-control form-control-sm mb-2"
            rows={3}
            placeholder={`Why do you want to work at ${job.company}?\nTell us about a project you're proud of.`}
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            aria-label="Application questions"
          />
          {integrations.ai ? (
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <button className="btn btn-sm btn-outline-primary" onClick={writeAnswers} disabled={writing || !questionText.trim()}>
                {writing ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-stars me-1" />}
                {writing ? "Writing answers…" : answers.length ? "Rewrite answers" : "Write answers"}
              </button>
              <UsageLeft />
            </div>
          ) : (
            <AccessNotice feature="Drafting answers" className="mb-3" />
          )}
          {answers.length > 0 && (
            <div className="d-flex flex-column gap-2 mb-4">
              {answers.map((a, i) => (
                <div key={a.question} className="field-row status-review">
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                    <label className="small fw-medium mb-0" htmlFor={`a-${i}`}>
                      {a.question}
                    </label>
                    <CopyButton text={a.answer} label="Copy answer" />
                  </div>
                  <textarea
                    id={`a-${i}`}
                    className="form-control form-control-sm"
                    rows={4}
                    value={a.answer}
                    onChange={(e) =>
                      setAnswers((list) => list.map((x, j) => (j === i ? { ...x, answer: e.target.value } : x)))
                    }
                  />
                </div>
              ))}
            </div>
          )}

          <h2 className="h6 mb-2">Attachments</h2>
          <div className="d-flex flex-column gap-2 mb-4">
            <div className="ready-item">
              <i className="bi bi-file-earmark-person text-primary" />
              <div className="flex-grow-1 small">
                <div className="fw-medium">Resume</div>
                <div className="text-muted">
                  {job.tailoredResume ? `Tailored for ${job.company}` : defaultResume ? defaultResume.name : "No resume uploaded"}
                </div>
              </div>
              {job.tailoredResume ? (
                tailoredBase?.isWord ? (
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => downloadFile("resume")}
                    disabled={!!downloading}
                    aria-label="Download tailored resume as PDF"
                  >
                    {downloading === "resume" ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-file-earmark-pdf" />}
                  </button>
                ) : (
                  <button className="btn btn-sm btn-light" onClick={downloadTailoredText} title="Upload the Word version of your resume to get a PDF">
                    <i className="bi bi-download" />
                  </button>
                )
              ) : (
                defaultResume?.hasFile && (
                  <button className="btn btn-sm btn-light" onClick={() => downloadResume(defaultResume)}>
                    <i className="bi bi-download" />
                  </button>
                )
              )}
            </div>
            <div className="ready-item">
              <i className="bi bi-envelope-paper text-primary" />
              <div className="flex-grow-1 small">
                <div className="fw-medium">Cover letter</div>
                <div className="text-muted">
                  {job.coverLetter ? "Ready" : job.noCoverLetter ? "Not required for this job" : "Not written yet"}
                </div>
              </div>
              {job.noCoverLetter && !job.coverLetter ? null : job.coverLetter ? (
                <div className="d-flex gap-1">
                  <CopyButton text={job.coverLetter} label="Copy cover letter" />
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => downloadFile("cover")}
                    disabled={!!downloading}
                    aria-label="Download cover letter as PDF"
                  >
                    {downloading === "cover" ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-file-earmark-pdf" />}
                  </button>
                </div>
              ) : (
                <Link to={`/jobs/${job.id}?tab=cover-letter`} className="btn btn-sm btn-light">
                  Write one
                </Link>
              )}
            </div>
            <div className="ready-item">
              <i className="bi bi-mortarboard text-primary" />
              <div className="flex-grow-1 small">
                <div className="fw-medium">Transcript</div>
                <div className="text-muted">{transcript ? transcript.name : "Only if the application asks for one"}</div>
              </div>
              {transcript ? (
                <button className="btn btn-sm btn-light" onClick={() => downloadTranscript(transcript)} aria-label="Download transcript">
                  <i className="bi bi-download" />
                </button>
              ) : (
                <Link to="/documents#transcript" className="btn btn-sm btn-light">
                  Upload
                </Link>
              )}
            </div>
          </div>

          <div className="d-flex flex-column flex-sm-row gap-2">
            <button className="btn btn-light" onClick={() => setPhase("idle")}>
              Back
            </button>
            {job.url && (
              <a
                className={`btn ${opened ? "btn-light" : "btn-primary"} ms-sm-auto`}
                href={job.applyUrl || job.url}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpened(true)}
              >
                <i className="bi bi-box-arrow-up-right me-1" /> Open application
              </a>
            )}
            {(opened || !job.url) && (
              <button className={`btn btn-success ${job.url ? "" : "ms-sm-auto"}`} onClick={submit}>
                <i className="bi bi-check2-circle me-1" /> I submitted it
              </button>
            )}
          </div>
          {opened && (
            <p className="small text-muted text-sm-end mt-2 mb-0">
              Fill in the form in the other tab, submit it there, then come back and confirm.
            </p>
          )}
        </div>
      </div>
    );
  }

  const openApp = async () => {
    setOpening(true);
    const result = await openApplication(job);
    setOpening(false);
    const [message, variant] = openMessage(result);
    toast(message, variant);
  };

  const questions = questionStats(job);
  const applyLink = job.applyUrl || job.url;

  return (
    <>
      {questions.total > 0 && <QuestionsCard job={job} />}
      <div className="card">
        <div className="card-body">
          <h2 className="h6 mb-1">Get ready to apply</h2>
          <p className="text-muted small mb-3">
            We gather everything the application will ask for (your details, answers to its questions, resume and cover
            letter) so filling it in takes a minute. Nothing is submitted without you.
          </p>

          <div className="d-flex flex-column gap-2 mb-4">
            <ReadyItem
              ok={profileReady}
              label="Your profile"
              detail={profileReady ? "Name, email and phone are set" : "Add your name, email and phone"}
              fixTo="/profile"
              fixLabel="Complete"
            />
            <ReadyItem
              ok={!!defaultResume}
              label="Resume"
              detail={job.tailoredResume ? `Tailored for ${job.company}` : defaultResume ? "Using your default resume (not tailored yet)" : "No resume uploaded"}
              fixTo="/documents"
              fixLabel="Upload"
            />
            {job.noCoverLetter && !job.coverLetter ? (
              <ReadyItem neutral label="Cover letter" detail="Not required for this job" />
            ) : (
              <ReadyItem
                ok={!!job.coverLetter}
                label="Cover letter"
                detail={job.coverLetter ? "Ready" : "Optional, but recommended"}
                fixTo={`/jobs/${job.id}?tab=cover-letter`}
                fixLabel="Write one"
              />
            )}
            {questions.total > 0 && (
              <ReadyItem
                ok={questions.needsYou === 0}
                label="Application questions"
                detail={
                  questions.needsYou
                    ? `${questions.needsYou} still need your answer (above)`
                    : `All ${questions.total} answered. Check them above`
                }
              />
            )}
            <ReadyItem
              ok={!!transcript}
              neutral
              label="Transcript"
              detail={transcript ? transcript.name : "Only if the application asks for one"}
              fixTo="/documents#transcript"
              fixLabel="Upload"
            />
          </div>

          <div className="d-flex flex-column flex-sm-row gap-2">
            {applyLink && (
              <button className="btn btn-primary" onClick={openApp} disabled={opening}>
                {opening ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-box-arrow-up-right me-1" />}
                Open application
              </button>
            )}
            <button className={`btn ${applyLink ? "btn-light" : "btn-primary"}`} onClick={start}>
              <i className="bi bi-magic me-1" /> Application kit
            </button>
            <button className="btn btn-light" onClick={submit}>
              I already applied
            </button>
          </div>
          {applyLink && (
            <p className="small text-muted mt-2 mb-0">
              With the Chrome extension connected, Open application fills in the form for you. The kit has everything to copy
              and paste instead.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

export default ApplyTab;
